import { z } from 'zod'

import { type Locale, type LocaleContext, type Localized, LocalizedSchema, resolveText } from './common'

// Content types (issue #72): the sections of a site's content that a customer can rename, switch off and
// (later) add to. Every site has the four built-in ones; a row in `content_types` can override their names.

/** How a type's content is laid out and edited. */
export const TEMPLATES = ['catalogue', 'cards', 'articles'] as const
export const TemplateSchema = z.enum(TEMPLATES)
export type Template = z.infer<typeof TemplateSchema>

export const BUILTIN_TYPE_KEYS = ['products', 'industries', 'journal', 'news'] as const
export type BuiltinTypeKey = (typeof BUILTIN_TYPE_KEYS)[number]

/** The words a type is called by: one item and many, plus (catalogues only) one category and many. */
export const TypeNamesSchema = z.object({
  singular: LocalizedSchema.default({}),
  plural: LocalizedSchema.default({}),
  categorySingular: LocalizedSchema.default({}),
  categoryPlural: LocalizedSchema.default({}),
})
export type TypeNames = z.infer<typeof TypeNamesSchema>

type Words = { singular: string; plural: string; categorySingular: string; categoryPlural: string }

/** Built-in types: template, URL segment and the names used until a site overrides them. */
export const BUILTIN_TYPES: Record<BuiltinTypeKey, { template: Template; slug: string; sortOrder: number; names: Record<Locale, Words> }> = {
  products: {
    template: 'catalogue',
    slug: 'products',
    sortOrder: 10,
    names: {
      en: { singular: 'Product', plural: 'Products', categorySingular: 'Category', categoryPlural: 'Categories' },
      id: { singular: 'Produk', plural: 'Produk', categorySingular: 'Kategori', categoryPlural: 'Kategori' },
      zh: { singular: '产品', plural: '产品', categorySingular: '分类', categoryPlural: '分类' },
      vi: { singular: 'Sản phẩm', plural: 'Sản phẩm', categorySingular: 'Danh mục', categoryPlural: 'Danh mục' },
      ja: { singular: '製品', plural: '製品', categorySingular: 'カテゴリー', categoryPlural: 'カテゴリー' },
    },
  },
  industries: {
    template: 'cards',
    slug: 'industries',
    sortOrder: 20,
    names: {
      en: { singular: 'Industry', plural: 'Industries', categorySingular: '', categoryPlural: '' },
      id: { singular: 'Industri', plural: 'Industri', categorySingular: '', categoryPlural: '' },
      zh: { singular: '行业', plural: '行业', categorySingular: '', categoryPlural: '' },
      vi: { singular: 'Ngành', plural: 'Ngành', categorySingular: '', categoryPlural: '' },
      ja: { singular: '業界', plural: '業界', categorySingular: '', categoryPlural: '' },
    },
  },
  journal: {
    template: 'articles',
    slug: 'journal',
    sortOrder: 30,
    names: {
      en: { singular: 'Journal article', plural: 'Journal', categorySingular: '', categoryPlural: '' },
      id: { singular: 'Artikel jurnal', plural: 'Jurnal', categorySingular: '', categoryPlural: '' },
      zh: { singular: '专栏文章', plural: '专栏', categorySingular: '', categoryPlural: '' },
      vi: { singular: 'Bài viết chuyên mục', plural: 'Chuyên mục', categorySingular: '', categoryPlural: '' },
      ja: { singular: 'コラム記事', plural: 'コラム', categorySingular: '', categoryPlural: '' },
    },
  },
  news: {
    template: 'articles',
    slug: 'news',
    sortOrder: 40,
    names: {
      en: { singular: 'News article', plural: 'News', categorySingular: '', categoryPlural: '' },
      id: { singular: 'Artikel berita', plural: 'Berita', categorySingular: '', categoryPlural: '' },
      zh: { singular: '新闻文章', plural: '新闻', categorySingular: '', categoryPlural: '' },
      vi: { singular: 'Bài tin tức', plural: 'Tin tức', categorySingular: '', categoryPlural: '' },
      ja: { singular: 'ニュース記事', plural: 'ニュース', categorySingular: '', categoryPlural: '' },
    },
  },
}

export const isBuiltinTypeKey = (key: string): key is BuiltinTypeKey => (BUILTIN_TYPE_KEYS as readonly string[]).includes(key)

const WORD_KEYS = ['singular', 'plural', 'categorySingular', 'categoryPlural'] as const

/**
 * A type's name in one language: the site's own wording (primary language, then any other) first, then the
 * built-in default in that language. Empty when there is neither.
 */
export function resolveTypeWords(key: string, names: TypeNames | null | undefined, ctx: LocaleContext): Words {
  const builtin = isBuiltinTypeKey(key) ? BUILTIN_TYPES[key].names[ctx.locale] : null
  return Object.fromEntries(
    WORD_KEYS.map((w) => {
      const own = names ? resolveText(names[w] as Localized, ctx) : ''
      return [w, own || builtin?.[w] || '']
    }),
  ) as Words
}

/** A content type as the CMS edits it. */
export const AdminContentTypeSchema = z.object({
  key: z.string(),
  template: TemplateSchema,
  /** URL segment on the public site. */
  slug: z.string(),
  builtin: z.boolean(),
  enabled: z.boolean(),
  sortOrder: z.number().int(),
  /** The site's own wording only; empty fields fall back to the defaults in `defaults`. */
  names: TypeNamesSchema,
  /** Built-in default wording per language (empty for custom types). */
  defaults: z.record(z.string(), z.object({ singular: z.string(), plural: z.string(), categorySingular: z.string(), categoryPlural: z.string() })),
  counts: z.object({ items: z.number().int(), categories: z.number().int() }),
  /** Set when the section was deleted; it can still be restored. Only in the "deleted" list. */
  deletedAt: z.string().nullable().default(null),
})
export type AdminContentType = z.infer<typeof AdminContentTypeSchema>
export const AdminContentTypeListSchema = z.object({ items: z.array(AdminContentTypeSchema) })

export const UpdateContentTypeRequestSchema = z.object({
  names: TypeNamesSchema.optional(),
  enabled: z.boolean().optional(),
  /** New address of a custom section; the old one redirects. Built-in sections keep theirs. */
  slug: z.string().min(1).max(40).optional(),
})
export type UpdateContentTypeRequest = z.infer<typeof UpdateContentTypeRequestSchema>

/** A content type on the public site, in the requested language. */
export const PublicContentTypeSchema = z.object({
  key: z.string(),
  template: TemplateSchema,
  slug: z.string(),
  singular: z.string(),
  plural: z.string(),
  categorySingular: z.string(),
  categoryPlural: z.string(),
})
export type PublicContentType = z.infer<typeof PublicContentTypeSchema>
